'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import MediaWithFallback from '@/components/ui/MediaWithFallback/MediaWithFallback';
import CmsRichTextHeading, { hasRichTextContent } from '@/components/ui/CmsRichTextHeading';

interface ProductsHeroEnquiryProps {
  /** CMS `bannerTitle` — small uppercase eyebrow above the headline. */
  eyebrow?: string;
  /** CMS `bannerSubtitle` — the large hero headline. */
  headline?: string;
  /** CMS `titleRichText` — preferred formatted hero headline. */
  headlineRichText?: string | null;
  /** CMS `bannerHeroImage.url` — may be an image OR an mp4. */
  heroMediaUrl?: string;
  /** CMS `bannerHeroImage.mime` — used to decide image vs video routing. */
  heroMediaMime?: string;
  /** Optional dedicated poster URL when `heroMediaUrl` is a video. */
  heroPosterUrl?: string;
}

/** Resolve the hero media into a video URL and/or image URL by mime/extension. */
function resolveHeroMedia(
  heroMediaUrl: string | undefined,
  heroMediaMime: string | undefined,
  heroPosterUrl: string | undefined,
) {
  const isVideo = Boolean(
    heroMediaUrl &&
    (heroMediaMime?.startsWith('video/') || /\.(mp4|webm|mov)(\?|$)/i.test(heroMediaUrl)),
  );
  return {
    isVideo,
    videoUrl: isVideo ? heroMediaUrl : undefined,
    imageUrl: isVideo ? heroPosterUrl : heroMediaUrl,
  };
}

/** Eyebrow + headline block, or null when neither is present. */
function HeroTextBlock({
  eyebrowText,
  headlineText,
  headlineRichText,
}: {
  eyebrowText?: string;
  headlineText?: string;
  headlineRichText?: string | null;
}) {
  if (!eyebrowText && !headlineText && !hasRichTextContent(headlineRichText)) return null;
  return (
    <div className="max-w-[920px]">
      {eyebrowText ? (
        <p className="text-[11px] font-medium uppercase leading-normal tracking-[0.18em] text-white sm:text-sm md:text-[16px] md:tracking-[0.2em]">
          {eyebrowText}
        </p>
      ) : null}
      {headlineText || hasRichTextContent(headlineRichText) ? (
        <CmsRichTextHeading
          className="hero-title mt-4 text-white md:mt-5"
          html={headlineRichText}
          fallback={headlineText}
        />
      ) : null}
    </div>
  );
}

export default function ProductsHeroEnquiry({
  eyebrow,
  headline,
  headlineRichText,
  heroMediaUrl,
  heroMediaMime,
  heroPosterUrl,
}: ProductsHeroEnquiryProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const fixedMediaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    if (!sectionRef.current || !fixedMediaRef.current) return;

    gsap.registerPlugin(ScrollTrigger);

    const runFadeAnimation = () => {
      gsap.set(fixedMediaRef.current, { opacity: 1, scale: 1 });
      gsap.to(fixedMediaRef.current, {
        opacity: 0,
        scale: 1.06,
        transformOrigin: 'center center',
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: '-13% top',
          end: '40% top',
          scrub: 1,
        },
      });
    };

    const ctx = gsap.context(runFadeAnimation, sectionRef);

    return () => {
      ctx.revert();
    };
  }, []);

  const eyebrowText = eyebrow?.trim();
  const headlineText = headline?.trim();
  const hasRichHeadline = hasRichTextContent(headlineRichText);

  const { videoUrl, imageUrl } = resolveHeroMedia(heroMediaUrl, heroMediaMime, heroPosterUrl);
  const heroAlt = eyebrowText || headlineText || 'Wonder Cement products';

  const heroTextContent =
    eyebrowText || headlineText || hasRichHeadline ? (
      <HeroTextBlock
        eyebrowText={eyebrowText}
        headlineText={headlineText}
        headlineRichText={headlineRichText}
      />
    ) : null;

  return (
    <section className="relative w-full overflow-hidden bg-black">
      {/* Mobile / tablet: image first, content below */}
      <div className="flex flex-col bg-black text-white lg:hidden">
        {imageUrl || videoUrl ? (
          <MediaWithFallback
            videoUrl={videoUrl}
            imageUrl={imageUrl}
            alt={heroAlt}
            className="w-full"
            imageClassName="h-auto w-full object-cover object-[center_35%]"
            videoClassName="h-auto w-full object-cover object-[center_35%]"
            width={1440}
            height={900}
            priority
            autoPlay
            muted
            loop
            playsInline
            poster={heroPosterUrl}
          />
        ) : null}
        {heroTextContent ? (
          <div className="px-5 py-10 sm:px-8 sm:py-12 md:px-12">{heroTextContent}</div>
        ) : null}
      </div>

      {/* Desktop: full-viewport hero with content lifted from bottom (infrastructure pattern) */}
      <div
        ref={sectionRef}
        className="relative hidden min-h-[600px] h-[100dvh] w-full overflow-hidden bg-black text-white lg:block"
      >
        <div ref={fixedMediaRef} className="fixed left-0 top-0 z-0 h-[calc(100vh-40px)] w-full">
          {imageUrl || videoUrl ? (
            <MediaWithFallback
              videoUrl={videoUrl}
              imageUrl={imageUrl}
              alt={heroAlt}
              className="h-full w-full"
              imageClassName="object-cover object-center"
              videoClassName="object-cover object-center"
              fill
              priority
              sizes="(min-width: 1920px) 1920px, 100vw"
              autoPlay
              muted
              loop
              playsInline
              poster={heroPosterUrl}
            />
          ) : null}
        </div>

        <div className="pointer-events-none absolute inset-0 z-[1] bg-black/10" aria-hidden />
        <div
          className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.55)_22%,rgba(0,0,0,0.2)_42%,transparent_72%)]"
          aria-hidden
        />

        <div className="relative z-10 mx-auto flex h-full flex-col justify-end container py-12 md:py-14 lg:py-16 max-sm:px-4">
          {heroTextContent}
        </div>
      </div>
    </section>
  );
}
